import { useEffect, useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import BottomNav from "@/components/BottomNav";
import ShareBar from "@/components/ShareBar";
import { api } from "@/lib/api";
import { Copy, Users, Sparkles, CheckCircle2, Lock } from "lucide-react";
import { toast } from "sonner";

const PERKS = [
  { key: "audio_narrator", label: "Audio Narrator", req: 1, desc: "Listen to any whitepaper in expert voice." },
  { key: "persona_mimicking", label: "Persona Mimicking", req: 3, desc: "Teach like Feynman, Coco Chanel, Warren Buffett." },
  { key: "priority_ai", label: "Priority AI", req: 5, desc: "Fast-lane generation, richer models." },
];

export default function Referrals() {
  const [data, setData] = useState(null);
  const [credits, setCredits] = useState(0);

  useEffect(() => {
    api.get("/referrals/me").then(({ data }) => setData(data)).catch(() => {});
    api.get("/credits/balance").then(({ data }) => setCredits(data.credits)).catch(() => {});
  }, []);

  const link = data?.code
    ? `${typeof window !== "undefined" ? window.location.origin : ""}/?ref=${data.code}`
    : "";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Referral link copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  return (
    <div className="min-h-screen pb-20 md:pb-0" data-testid="referrals-page">
      <Header credits={credits} onOpenAd={() => {}} onOpenCheckout={() => {}} />
      <main className="max-w-5xl mx-auto px-6 md:px-10 py-10 space-y-6">
        <div>
          <div className="label-eyebrow mb-2 flex items-center gap-2">
            <Users size={12} className="text-[#00FF9D]" /> Referral Dashboard
          </div>
          <h1 className="font-display text-3xl md:text-4xl tracking-tight">Invite & unlock premium voices.</h1>
          <p className="text-white/55 text-sm max-w-xl mt-1">
            Every friend who joins with your code unlocks a premium feature for your account — permanently.
          </p>
        </div>

        <section className="glass p-6" data-testid="referral-code-card">
          <div className="label-eyebrow mb-2">Your referral code</div>
          <div className="flex flex-wrap items-center gap-3">
            <code className="font-mono-oak text-3xl md:text-4xl tracking-[0.25em] text-[#00FF9D] bg-black/40 rounded-xl px-5 py-3 border border-[#00FF9D]/30">
              {data?.code || "…"}
            </code>
            <button
              onClick={copy}
              className="btn-mint-outline inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs"
              data-testid="btn-copy-code"
            >
              <Copy size={12} /> Copy link
            </button>
          </div>
          <div className="mt-5">
            <ShareBar url={link} title="Master anything on Oak AI Campus" text="Try Oak AI Campus — I'm learning with AI-guided 30-min paths. Join with my code:" />
          </div>
        </section>

        <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {PERKS.map((p) => {
            const unlocked = data?.unlocks?.[p.key];
            const invited = data?.invited ?? 0;
            return (
              <div
                key={p.key}
                className={`glass p-5 relative overflow-hidden transition ${
                  unlocked ? "glow-mint border-[#00FF9D]/30" : ""
                }`}
                data-testid={`perk-${p.key}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  {unlocked ? (
                    <CheckCircle2 size={16} className="text-[#00FF9D]" />
                  ) : (
                    <Lock size={16} className="text-white/40" />
                  )}
                  <div className="font-display text-lg tracking-tight">{p.label}</div>
                </div>
                <div className="text-white/55 text-sm">{p.desc}</div>
                <div className="mt-4 flex items-center justify-between text-[11px]">
                  <span className="label-eyebrow !text-[10px]">Requires {p.req} invites</span>
                  <span className={`font-mono-oak ${unlocked ? "text-[#00FF9D]" : "text-white/40"}`}>
                    {invited}/{p.req}
                  </span>
                </div>
                <div className="mt-2 h-1 rounded-full bg-white/5 overflow-hidden">
                  <div
                    className="h-full transition-all"
                    style={{
                      width: `${Math.min(100, (invited / p.req) * 100)}%`,
                      background: unlocked ? "#00FF9D" : "linear-gradient(90deg,#5D3FD3,#FF7B00)",
                    }}
                  />
                </div>
              </div>
            );
          })}
        </section>

        <section className="glass p-6 flex items-center gap-3" data-testid="invite-stat">
          <Sparkles size={16} className="text-[#ffb067]" />
          <div>
            <div className="font-display text-lg">You've invited <span className="text-[#00FF9D]">{data?.invited ?? 0}</span> learner{(data?.invited ?? 0) === 1 ? "" : "s"}</div>
            <div className="text-white/55 text-sm">Keep going — every invite lifts the whole OAK community.</div>
          </div>
        </section>
      </main>
      <Footer />
      <BottomNav />
    </div>
  );
}
